// React-free core of the auth store. The web apps import it through ./store.ts,
// which adds the useAuthStore hook; the Expo app imports this file directly and
// binds its own hook, so the store never drags the web's copy of React into the
// native bundle.
import { createStore, type StoreApi } from 'zustand/vanilla'
import type { AuthResponse, User, LinkedAccount, TrustScore, GameTrustScore, MeResponse, EmailChangeStatus } from './types.ts'
import type { PublicKeyCredentialCreationOptionsJSON, PublicKeyCredentialRequestOptionsJSON, RegistrationResponseJSON, AuthenticationResponseJSON } from '@simplewebauthn/browser'
import { loginApi, registerApi, refreshApi, logoutApi, getMeApi, passkeyLoginBeginApi, passkeyLoginFinishApi, passkeyRegisterBeginApi, passkeyRegisterFinishApi, acceptTermsApi, submitDateOfBirthApi, ApiError, type PasskeyClient } from './api.ts'
import { setAccessToken, getAccessToken, configureClient } from './client.ts'

/** The parts of the store that /auth/me fills in. */
function meState(data: MeResponse) {
  return {
    linkedAccounts: data.linked_accounts,
    trustScore: data.trust_score,
    gameTrustScores: data.game_trust_scores ?? [],
    phoneVerified: data.phone_verified,
    phoneLast4: data.phone_last4 ?? '',
    hasPasskey: data.has_passkey,
    termsAcceptedAt: data.terms_accepted_at,
    dateOfBirth: data.date_of_birth,
    isMinor: data.is_minor,
    needsWelcome: data.needs_welcome ?? false,
    emailChange: data.email_change ?? null,
  }
}

export interface AuthState {
  user: User | null
  linkedAccounts: LinkedAccount[]
  trustScore: TrustScore | null
  gameTrustScores: GameTrustScore[]
  phoneVerified: boolean
  phoneLast4: string
  hasPasskey: boolean
  termsAcceptedAt: string | null
  dateOfBirth: string | null
  isMinor: boolean
  /** A new signup who hasn't finished or skipped the welcome steps. */
  needsWelcome: boolean
  /** The email was changed and the new address isn't confirmed yet. */
  emailChange: EmailChangeStatus | null
  isAuthenticated: boolean
  isLoading: boolean

  sessionId: string | null
  refreshToken: string | null

  login: (identifier: string, password: string) => Promise<void>
  /** Take on a session the API issued some other way (securing an account from an emailed link). */
  signInWith: (res: AuthResponse) => Promise<void>
  loginWithPasskey: () => Promise<void>
  registerPasskey: (name?: string) => Promise<void>
  register: (email: string, username: string, password: string, gamerTag: string, termsAccepted: boolean, dateOfBirth: string, parentalConsent: boolean, subscribeToNewsletter: boolean, redirect?: string) => Promise<string>
  logout: () => Promise<void>
  refresh: () => Promise<boolean>
  loadUser: () => Promise<void>
  setLinkedAccounts: (accounts: LinkedAccount[]) => void
  acceptTerms: () => Promise<void>
  submitDateOfBirth: (dateOfBirth: string, parentalConsent: boolean) => Promise<void>
}

function storageKey(prefix: string, key: string): string {
  return `${prefix}_${key}`
}

/**
 * Where the session id and refresh token persist. Synchronous on purpose: the
 * store reads both while it is being created. `localStorage` satisfies it on the
 * web; the Expo app passes a Keychain/Keystore-backed adapter.
 */
export interface AuthStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

/**
 * How a runtime asks the device for a passkey. The web's default goes through
 * the browser (@simplewebauthn/browser); the Expo app passes the OS passkey API.
 */
export interface PasskeyCeremony {
  /** Sent on every passkey call so the API verifies against this runtime's relying party. */
  client?: PasskeyClient
  authenticate(options: PublicKeyCredentialRequestOptionsJSON): Promise<AuthenticationResponseJSON>
  register(options: PublicKeyCredentialCreationOptionsJSON): Promise<RegistrationResponseJSON>
}

const browserPasskeys: PasskeyCeremony = {
  async authenticate(optionsJSON) {
    const { startAuthentication } = await import('@simplewebauthn/browser')
    return startAuthentication({ optionsJSON })
  },
  async register(optionsJSON) {
    const { startRegistration } = await import('@simplewebauthn/browser')
    return startRegistration({ optionsJSON })
  },
}

export interface AuthStoreOptions {
  /** Key prefix for persisted credentials. Default `carousel`. */
  storagePrefix?: string
  /** Default `localStorage`. */
  storage?: AuthStorage
  /**
   * Origin that emailed links (verify email, reset password) point back to.
   * Defaults to the current page's origin — the web app that sent the request.
   * A runtime without one (the Expo app) passes the web app's URL, or leaves it
   * undefined to let the API fall back to its FRONTEND_URL.
   */
  redirectUrl?: string
  /** Default: the browser's WebAuthn API. */
  passkeys?: PasskeyCeremony
}

function defaultRedirectUrl(): string | undefined {
  return typeof window !== 'undefined' && window.location ? window.location.origin : undefined
}

export function createAuthStore(opts?: AuthStoreOptions): StoreApi<AuthState> {
  const prefix = opts?.storagePrefix ?? 'carousel'
  const storage = opts?.storage ?? localStorage
  const redirectUrl = opts && 'redirectUrl' in opts ? opts.redirectUrl : defaultRedirectUrl()
  const passkeys = opts?.passkeys ?? browserPasskeys
  const sessionKey = storageKey(prefix, 'session_id')
  const refreshKey = storageKey(prefix, 'refresh_token')
  const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(`${prefix}_auth_sync`) : null

  let refreshTimer: ReturnType<typeof setTimeout> | null = null

  // Coalesces concurrent refresh attempts onto a single network call. Refresh tokens
  // are single-use and rotated server-side, so two simultaneous refreshes (e.g. the
  // scheduleRefresh timer firing at the same time as an apiFetch 401 retry) would race:
  // the second presents an already-rotated token and gets rejected, which clears
  // credentials and logs the user out even though their session was still valid.
  let inFlightRefresh: Promise<boolean> | null = null

  function scheduleRefresh(expiresAt: string) {
    if (refreshTimer) clearTimeout(refreshTimer)
    const ms = new Date(expiresAt).getTime() - Date.now() - 60_000 // 1 min before expiry
    refreshTimer = setTimeout(() => { store.getState().refresh() }, Math.max(ms, 0))
  }

  function clearRefreshTimer() {
    if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null }
  }

  const store = createStore<AuthState>()((set, get) => ({
    user: null,
    linkedAccounts: [],
    trustScore: null,
    gameTrustScores: [],
    phoneVerified: false,
    phoneLast4: '',
    hasPasskey: false,
    termsAcceptedAt: null,
    dateOfBirth: null,
    isMinor: false,
    needsWelcome: false,
    emailChange: null,
    isAuthenticated: false,
    isLoading: true,

    sessionId: storage.getItem(sessionKey),
    refreshToken: storage.getItem(refreshKey),

    login: async (identifier, password) => {
      await get().signInWith(await loginApi({ identifier, password }))
    },

    signInWith: async (res) => {
      setAccessToken(res.access_token)
      storage.setItem(sessionKey, res.session_id)
      storage.setItem(refreshKey, res.refresh_token)
      set({
        user: res.user,
        isAuthenticated: true,
        sessionId: res.session_id,
        refreshToken: res.refresh_token,
      })
      scheduleRefresh(res.expires_at)
      // Fetch full profile (linked accounts, trust score, phone status)
      try {
        const data = await getMeApi(res.access_token)
        set(meState(data))
      } catch {
        // Non-critical — will load on next page refresh
      }
    },

    loginWithPasskey: async () => {
      const { challenge_id, options } = await passkeyLoginBeginApi(passkeys.client)
      const credential = await passkeys.authenticate(options)
      await get().signInWith(await passkeyLoginFinishApi(challenge_id, credential, passkeys.client))
    },

    registerPasskey: async (name = 'Passkey') => {
      const token = getAccessToken()
      if (!token) throw new Error('Not authenticated')
      const { challenge_id, options } = await passkeyRegisterBeginApi(name, token, passkeys.client)
      const credential = await passkeys.register(options)
      await passkeyRegisterFinishApi(challenge_id, name, credential, token, passkeys.client)
      set({ hasPasskey: true })
    },

    register: async (email, username, password, gamerTag, termsAccepted, dateOfBirth, parentalConsent, subscribeToNewsletter, redirect) => {
      const res = await registerApi({ email, username, password, gamer_tag: gamerTag, terms_accepted: termsAccepted, date_of_birth: dateOfBirth, parental_consent: parentalConsent, subscribe_to_newsletter: subscribeToNewsletter, redirect_url: redirectUrl, redirect })
      return res.email
    },

    logout: async () => {
      const { sessionId } = get()
      // Use the refresh token (state, falling back to storage) rather than the
      // access token — it may have expired while the tab was idle, but the server
      // still needs proof of ownership to revoke the session.
      const refreshToken = get().refreshToken ?? storage.getItem(refreshKey)
      try {
        if (sessionId && refreshToken) await logoutApi(sessionId, refreshToken)
      } catch {
        // Ignore logout errors — local state is cleared unconditionally below.
      }
      clearRefreshTimer()
      setAccessToken(null)
      storage.removeItem(sessionKey)
      storage.removeItem(refreshKey)
      set({
        user: null,
        linkedAccounts: [],
        trustScore: null,
        gameTrustScores: [],
        phoneVerified: false,
        phoneLast4: '',
        hasPasskey: false,
        termsAcceptedAt: null,
        dateOfBirth: null,
        isMinor: false,
        needsWelcome: false,
        emailChange: null,
        isAuthenticated: false,
        sessionId: null,
        refreshToken: null,
      })
      channel?.postMessage({ type: 'LOGGED_OUT' })
    },

    refresh: async () => {
      // Join an in-flight refresh rather than starting a competing one (token rotation race).
      if (inFlightRefresh) return inFlightRefresh
      inFlightRefresh = (async (): Promise<boolean> => {
        const { sessionId, refreshToken } = get()
        if (!sessionId || !refreshToken) return false
        try {
          const res = await refreshApi({ session_id: sessionId, refresh_token: refreshToken })
          setAccessToken(res.access_token)
          storage.setItem(sessionKey, res.session_id)
          storage.setItem(refreshKey, res.refresh_token)
          set({
            user: res.user,
            isAuthenticated: true,
            sessionId: res.session_id,
            refreshToken: res.refresh_token,
          })
          scheduleRefresh(res.expires_at)
          channel?.postMessage({ type: 'SESSION_UPDATED', sessionId: res.session_id, refreshToken: res.refresh_token, accessToken: res.access_token, user: res.user, expiresAt: res.expires_at })
          return true
        } catch (err) {
          // Only clear credentials on definitive auth failures (401, 403).
          // For network errors or server errors (5xx), preserve credentials so the user
          // can retry when the API comes back (e.g., during a deploy).
          const isAuthError = err instanceof ApiError && (err.status === 401 || err.status === 403)
          if (isAuthError) {
            clearRefreshTimer()
            setAccessToken(null)
            storage.removeItem(sessionKey)
            storage.removeItem(refreshKey)
            set({
              user: null,
              linkedAccounts: [],
              trustScore: null,
              gameTrustScores: [],
              phoneVerified: false,
              hasPasskey: false,
              termsAcceptedAt: null,
              dateOfBirth: null,
              isMinor: false,
              isAuthenticated: false,
              sessionId: null,
              refreshToken: null,
              isLoading: false,
            })
          }
          return false
        }
      })()
      try {
        return await inFlightRefresh
      } finally {
        inFlightRefresh = null
      }
    },

    loadUser: async () => {
      const { sessionId, refreshToken } = get()
      if (!sessionId || !refreshToken) {
        set({ isLoading: false })
        return
      }

      // Retry refresh with backoff for transient errors (e.g., API down during deploy)
      let ok = false
      for (let attempt = 0; attempt < 3; attempt++) {
        ok = await get().refresh()
        if (ok) break
        // If credentials were cleared (auth error), stop retrying
        if (!get().sessionId) break
        if (attempt < 2) await new Promise(r => setTimeout(r, 1000 * (attempt + 1)))
      }

      if (ok) {
        try {
          const token = getAccessToken()
          if (token) {
            const data = await getMeApi(token)
            set({ user: data.user, ...meState(data), isLoading: false })
          } else {
            set({ isLoading: false })
          }
        } catch {
          set({ isLoading: false })
        }
      } else {
        set({ isLoading: false })
      }
    },

    acceptTerms: async () => {
      const token = getAccessToken()
      if (!token) throw new Error('Not authenticated')
      await acceptTermsApi(token)
      set({ termsAcceptedAt: new Date().toISOString() })
    },

    submitDateOfBirth: async (dateOfBirth, parentalConsent) => {
      const token = getAccessToken()
      if (!token) throw new Error('Not authenticated')
      await submitDateOfBirthApi(dateOfBirth, parentalConsent, token)
      const now = new Date()
      const dob = new Date(dateOfBirth)
      let age = now.getFullYear() - dob.getFullYear()
      if (now.getMonth() < dob.getMonth() || (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate())) age--
      set({ dateOfBirth, isMinor: age < 18 })
    },

    setLinkedAccounts: (accounts) => set({ linkedAccounts: accounts }),
  }))

  // Cross-tab session synchronization
  if (channel) {
    channel.onmessage = (event: MessageEvent) => {
      const { type } = event.data as { type: string }
      if (type === 'SESSION_UPDATED') {
        const { sessionId, refreshToken, accessToken, user, expiresAt } = event.data as { sessionId: string; refreshToken: string; accessToken: string; user: User; expiresAt: string }
        setAccessToken(accessToken)
        storage.setItem(sessionKey, sessionId)
        storage.setItem(refreshKey, refreshToken)
        store.setState({ user, isAuthenticated: true, sessionId, refreshToken, isLoading: false })
        scheduleRefresh(expiresAt)
      } else if (type === 'LOGGED_OUT') {
        clearRefreshTimer()
        setAccessToken(null)
        storage.removeItem(sessionKey)
        storage.removeItem(refreshKey)
        store.setState({
          user: null, linkedAccounts: [], trustScore: null, gameTrustScores: [],
          phoneVerified: false, phoneLast4: '', hasPasskey: false,
          termsAcceptedAt: null, dateOfBirth: null, isMinor: false, needsWelcome: false, emailChange: null,
          isAuthenticated: false, sessionId: null, refreshToken: null,
        })
      }
    }
  }

  // Wire up the client's refresh callback to break the circular dependency
  configureClient({ refresh: () => store.getState().refresh() })

  return store
}

// ── Singleton ──

let _store: StoreApi<AuthState> | null = null

/** Call once at app startup (in main.tsx) before rendering. */
export function initAuthStore(opts?: AuthStoreOptions): StoreApi<AuthState> {
  _store = createAuthStore(opts)
  return _store
}

/** Direct store access — use outside React (e.g., in API client callbacks). */
export function getAuthStore(): StoreApi<AuthState> {
  if (!_store) throw new Error('@carouselgg/auth: call initAuthStore() before accessing store')
  return _store
}
